import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { 
  X, 
  MessageCircle, 
  Users, 
  Type, 
  Volume2, 
  Paintbrush, 
  Eye,
  Clock,
  Trophy,
  Ship,
  Download,
  CheckCircle,
  AlertCircle,
  BookOpen,
  Brain,
  Target
} from 'lucide-react';
import ChatModal from './ChatModal';
import CategorySelection from './CategorySelection';
import ModeSelection from './ModeSelection';
import StakeSetting from './StakeSetting';
import WaitingRoom from './WaitingRoom';
import LiveCompetition from './LiveCompetition';
import ShipAnimation from './ShipAnimation';
import useAutoScroll from '../../hooks/useAutoScroll';
import ViewProfileModal from './ViewProfileModal';
import capeService, { CAPEQuestion, CAPEContent, TaxonomyNode } from '../../services/capeService';

// Enhanced Types with CAPE integration
interface User {
  id: string;
  name: string;
  accountType: 'student' | 'public';
  gradeLevel?: number;
  university?: string;
  rating: number;
  wins: number;
  totalMatches: number;
  avatar?: string;
  profilePicture?: string;
  isOnline: boolean;
}

interface Competitor extends User {
  lastSeen?: string;
  currentStake?: number;
}

interface CAPEEnhancedQuestion extends CAPEQuestion {
  time: number;
  category: string;
  subcategory: string;
}

interface CAPECompetitionData {
  id: string;
  competitor: Competitor;
  category: string;
  subcategory: string;
  module: string;
  submodule: string;
  topic: string;
  subtopic: string;
  mode: string;
  stake: number;
  questions: CAPEEnhancedQuestion[];
  content: CAPEContent[];
  prize: number;
  appFee: number;
  capeMetadata: {
    totalContent: number;
    excludedCount: number;
    kenyaContext: boolean;
  };
}

type ChallengeStep = 'dashboard' | 'chat' | 'categories' | 'mode' | 'stake' | 'waiting' | 'competition' | 'evaluation';

// Styled Components
const InterfaceOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  z-index: 1000;
  overflow-y: auto;
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: white;
  margin: 0;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const CAPEStatusCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const CAPEStatusTitle = styled.h3`
  color: white;
  margin: 0 0 10px 0;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CAPEStatusText = styled.p`
  color: rgba(255, 255, 255, 0.8);
  margin: 5px 0;
  font-size: 0.9rem;
`;

const TopicSelector = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const TopicGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-top: 15px;
`;

const TopicCard = styled.div<{ selected?: boolean }>`
  background: ${props => props.selected ? 'rgba(46, 204, 113, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid ${props => props.selected ? 'rgba(46, 204, 113, 0.5)' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 10px;
  padding: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: white;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }
`;

const TopicTitle = styled.h4`
  margin: 0 0 5px 0;
  font-size: 1rem;
`;

const TopicMeta = styled.div`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  color: white;
`;

const ErrorMessage = styled.div`
  background: rgba(231, 76, 60, 0.2);
  border: 1px solid rgba(231, 76, 60, 0.5);
  border-radius: 10px;
  padding: 15px;
  color: white;
  margin: 20px 0;
`;

const SuccessMessage = styled.div`
  background: rgba(46, 204, 113, 0.2);
  border: 1px solid rgba(46, 204, 113, 0.5);
  border-radius: 10px;
  padding: 15px;
  color: white;
  margin: 20px 0;
`;

const CompetitionInterface: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  competitor: Competitor;
  challengeData?: any;
}> = ({ isOpen, onClose, competitor, challengeData }) => {
  const [currentStep, setCurrentStep] = useState<ChallengeStep>('dashboard');
  const [competitionData, setCompetitionData] = useState<CAPECompetitionData | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [capeStatus, setCapeStatus] = useState<{
    initialized: boolean;
    availableTopics: TaxonomyNode[];
    loading: boolean;
    error: string | null;
  }>({
    initialized: false,
    availableTopics: [],
    loading: false,
    error: null
  });
  const [selectedTopic, setSelectedTopic] = useState<TaxonomyNode | null>(null);
  const [challengeContent, setChallengeContent] = useState<{
    content: CAPEContent[];
    questions: CAPEQuestion[];
    loading: boolean;
    error: string | null;
  }>({
    content: [],
    questions: [],
    loading: false,
    error: null
  });

  // Initialize CAPE when component mounts
  useEffect(() => {
    if (isOpen) {
      initializeCAPE();
    }
  }, [isOpen]);

  const initializeCAPE = async () => {
    setCapeStatus(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      // Initialize CAPE service
      await capeService.initialize();
      
      // Use personalized content if available, otherwise fall back to general topics
      let topics: TaxonomyNode[] = [];
      
      if (challengeData && challengeData.topic) {
        // Use the personalized topic from CAPEIntegration
        console.log('Using personalized CAPE content:', challengeData);
        topics = [challengeData.topic];
      } else {
        // Fall back to general topics
        console.log('No personalized content, using general topics');
        topics = await capeService.searchTaxonomy(competitor.university || 'medicine', 20);
      }
      
      setCapeStatus({
        initialized: true,
        availableTopics: topics,
        loading: false,
        error: null
      });
    } catch (error) {
      console.error('Error initializing CAPE:', error);
      setCapeStatus(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to initialize CAPE service'
      }));
    }
  };

  const handleTopicSelect = async (topic: TaxonomyNode) => {
    setSelectedTopic(topic);
    setChallengeContent(prev => ({ ...prev, loading: true, error: null }));

    try {
      let result: any = null;
      let contentToUse: CAPEContent[] = [];
      let questionsToUse: CAPEQuestion[] = [];

      // Use personalized content if available, otherwise fetch general content
      if (challengeData && challengeData.questions && challengeData.content) {
        console.log('Using personalized challenge data:', challengeData);
        contentToUse = challengeData.content;
        questionsToUse = challengeData.questions;
        setChallengeContent({
          content: contentToUse,
          questions: questionsToUse,
          loading: false,
          error: null
        });
      } else {
        // Fall back to fetching general content
        console.log('No personalized content, fetching general content');
        result = await capeService.getContentForChallenge({
          subtopic: topic.subtopic,
          difficulty: topic.metadata.difficulty_default as any || 'Intermediate',
          competitorId: competitor.id,
          count: 10,
          questionTypes: ['mcq', 'true_false'],
          kenyaContext: true
        });

        contentToUse = result.content;
        questionsToUse = result.questions;
        setChallengeContent({
          content: contentToUse,
          questions: questionsToUse,
          loading: false,
          error: null
        });
      }

      // Create competition data using the loaded content
      const enhancedQuestions: CAPEEnhancedQuestion[] = questionsToUse.map(q => {
        // Ensure questions have proper options based on their type
        let options = q.options;
        
        // If no options provided, generate them based on question type
        if (!options || options.length === 0) {
          if (q.type === 'true_false') {
            options = ['True', 'False'];
          } else if (q.type === 'mcq') {
            // For MCQ without options, provide default options
            options = ['Option A', 'Option B', 'Option C', 'Option D'];
          }
          // For other types (fill_blank, short_answer, essay), leave options undefined
        }
        
        const enhancedQuestion = {
          ...q,
          options,
          time: 30, // 30 seconds per question
          category: topic.category,
          subcategory: topic.subcategory
        };
        
        console.log('Enhanced question:', {
          id: enhancedQuestion.id,
          type: enhancedQuestion.type,
          hasOptions: !!enhancedQuestion.options,
          optionsLength: enhancedQuestion.options?.length || 0,
          question: enhancedQuestion.question.substring(0, 50) + '...'
        });
        
        return enhancedQuestion;
      });

      const competitionData: CAPECompetitionData = {
        id: `cape_challenge_${Date.now()}`,
        competitor,
        category: topic.category,
        subcategory: topic.subcategory,
        module: topic.module,
        submodule: topic.submodule,
        topic: topic.topic,
        subtopic: topic.subtopic,
        mode: 'cape_enhanced',
        stake: 10,
        questions: enhancedQuestions,
        content: contentToUse,
        prize: enhancedQuestions.length * 2,
        appFee: enhancedQuestions.length * 0.2,
        capeMetadata: {
          totalContent: result?.total_available || contentToUse.length,
          excludedCount: result?.excluded_count || 0,
          kenyaContext: true
        }
      };

      setCompetitionData(competitionData);
    } catch (error) {
      console.error('Error getting challenge content:', error);
      setChallengeContent(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load challenge content'
      }));
    }
  };

  // Timer effect for questions
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (currentStep === 'competition' && timeLeft > 0) {
      timer = setTimeout(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    } else if (timeLeft === 0 && competitionData) {
      // Auto-submit when time runs out
      handleAnswerSubmit();
    }
    return () => clearTimeout(timer);
  }, [timeLeft, currentStep]);

  // Reset timer when question changes
  useEffect(() => {
    if (competitionData && competitionData.questions[currentQuestion]) {
      setTimeLeft(competitionData.questions[currentQuestion].time || 30);
      setSelectedAnswer(null);
    }
  }, [currentQuestion, competitionData]);

  const handleAnswerSelect = (answer: string) => {
    setSelectedAnswer(answer);
  };

  const handleAnswerSubmit = () => {
    if (competitionData && currentQuestion < competitionData.questions.length - 1) {
      // Move to next question
      setCurrentQuestion(prev => prev + 1);
    } else {
      // Competition complete
      handleCompetitionComplete({
        score: 0, // This would be calculated based on correct answers
        totalQuestions: competitionData?.questions.length || 0,
        correctAnswers: 0 // This would be calculated
      });
    }
  };

  const handleStartCompetition = () => {
    if (competitionData) {
      setCurrentQuestion(0);
      setTimeLeft(competitionData.questions[0]?.time || 30);
      setSelectedAnswer(null);
      setCurrentStep('competition');
    }
  };

  const handleStepChange = (step: ChallengeStep) => {
    setCurrentStep(step);
  };

  const handleCompetitionComplete = (results: any) => {
    console.log('Competition completed:', results);
    setCurrentStep('evaluation');
  };

  if (!isOpen) return null;

  return (
    <InterfaceOverlay>
      <ContentContainer>
        <Header>
          <Title>
            <Brain size={28} style={{ marginRight: '10px' }} />
            CAPE-Enhanced Competition
          </Title>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </Header>

        {/* CAPE Status */}
        <CAPEStatusCard>
          <CAPEStatusTitle>
            <BookOpen size={20} />
            CAPE Content Engine Status
          </CAPEStatusTitle>
          {capeStatus.loading && (
            <CAPEStatusText>🔄 Initializing CAPE service...</CAPEStatusText>
          )}
          {capeStatus.error && (
            <ErrorMessage>
              <AlertCircle size={16} style={{ marginRight: '8px' }} />
              {capeStatus.error}
            </ErrorMessage>
          )}
          {capeStatus.initialized && !capeStatus.loading && (
            <>
              <CAPEStatusText>✅ CAPE service initialized successfully</CAPEStatusText>
              <CAPEStatusText>📚 Found {capeStatus.availableTopics.length} available topics</CAPEStatusText>
              <CAPEStatusText>🎯 Ready to generate personalized challenges</CAPEStatusText>
            </>
          )}
        </CAPEStatusCard>

        {/* Topic Selection */}
        {capeStatus.initialized && currentStep === 'dashboard' && (
          <TopicSelector>
            <h3 style={{ color: 'white', margin: '0 0 15px 0' }}>
              <Target size={20} style={{ marginRight: '8px' }} />
              Select Your Challenge Topic
            </h3>
            <p style={{ color: 'rgba(255, 255, 255, 0.8)', margin: '0 0 15px 0' }}>
              Choose a topic to start your CAPE-enhanced learning challenge
            </p>
            
            {challengeContent.loading && (
              <LoadingSpinner>
                <div>🔄 Loading challenge content...</div>
              </LoadingSpinner>
            )}

            {challengeContent.error && (
              <ErrorMessage>
                <AlertCircle size={16} style={{ marginRight: '8px' }} />
                {challengeContent.error}
              </ErrorMessage>
            )}

            <TopicGrid>
              {capeStatus.availableTopics.map((topic) => (
                <TopicCard
                  key={topic.subtopic}
                  selected={selectedTopic?.subtopic === topic.subtopic}
                  onClick={() => handleTopicSelect(topic)}
                >
                  <TopicTitle>{topic.subtopic}</TopicTitle>
                  <TopicMeta>
                    <div>{topic.category} → {topic.subcategory}</div>
                    <div>Difficulty: {topic.metadata.difficulty_default || 'Intermediate'}</div>
                    <div>Skills: {topic.metadata.skills?.join(', ') || 'Recall'}</div>
                  </TopicMeta>
                </TopicCard>
              ))}
            </TopicGrid>

            {selectedTopic && challengeContent.content.length > 0 && (
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <SuccessMessage>
                  <CheckCircle size={16} style={{ marginRight: '8px' }} />
                  Ready! Found {challengeContent.content.length} content items and {challengeContent.questions.length} questions for "{selectedTopic.subtopic}"
                </SuccessMessage>
                <button
                  onClick={handleStartCompetition}
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '15px 30px',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    marginTop: '15px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  🚀 Start CAPE Challenge
                </button>
              </div>
            )}
          </TopicSelector>
        )}

        {/* Competition Interface */}
        {currentStep === 'competition' && competitionData && 
         currentQuestion >= 0 && currentQuestion < competitionData.questions.length &&
         competitionData.questions[currentQuestion] ? (
          <div>
            {(() => {
              try {
                console.log('Passing props to LiveCompetition:', {
                  questionNumber: currentQuestion + 1,
                  totalQuestions: competitionData.questions.length,
                  timeLeft,
                  selectedAnswer,
                  currentQuestion
                });
                return (
                  <LiveCompetition
                    key={`question-${currentQuestion}`}
                    question={competitionData.questions[currentQuestion]}
                    questionNumber={currentQuestion + 1}
                    totalQuestions={competitionData.questions.length}
                    timeLeft={timeLeft}
                    selectedAnswer={selectedAnswer}
                    onAnswerSelect={handleAnswerSelect}
                    onAnswerSubmit={handleAnswerSubmit}
                    onBack={() => setCurrentStep('dashboard')}
                  />
                );
              } catch (error) {
                console.error('Error rendering LiveCompetition:', error);
                return (
                  <div style={{ textAlign: 'center', color: 'white', padding: '50px' }}>
                    <h2>Error Loading Competition</h2>
                    <p>There was an error loading the competition interface.</p>
                    <button
                      onClick={() => setCurrentStep('dashboard')}
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '15px 30px',
                        fontSize: '1.1rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        marginTop: '15px'
                      }}
                    >
                      Go Back
                    </button>
                  </div>
                );
              }
            })()}
          </div>
        ) : currentStep === 'competition' && competitionData && (
          <div style={{ textAlign: 'center', color: 'white', padding: '50px' }}>
            <h2>Competition Complete!</h2>
            <p>All questions have been answered.</p>
            <button
              onClick={() => setCurrentStep('dashboard')}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                padding: '15px 30px',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: 'pointer',
                marginTop: '15px'
              }}
            >
              Return to Dashboard
            </button>
          </div>
        )}

        {/* Other steps remain the same */}
        {currentStep === 'chat' && (
          <ChatModal
            isOpen={true}
            onClose={() => setCurrentStep('dashboard')}
            competitor={competitor}
          />
        )}

        {currentStep === 'categories' && (
          <CategorySelection
            onNext={() => setCurrentStep('mode')}
            onBack={() => setCurrentStep('dashboard')}
          />
        )}

        {currentStep === 'mode' && (
          <ModeSelection
            onNext={() => setCurrentStep('stake')}
            onBack={() => setCurrentStep('categories')}
          />
        )}

        {currentStep === 'stake' && (
          <StakeSetting
            onNext={() => setCurrentStep('waiting')}
            onBack={() => setCurrentStep('mode')}
          />
        )}

        {currentStep === 'waiting' && (
          <WaitingRoom
            onStart={() => setCurrentStep('competition')}
            onBack={() => setCurrentStep('stake')}
          />
        )}

        {currentStep === 'evaluation' && (
          <div style={{ textAlign: 'center', color: 'white' }}>
            <h2>🎉 Challenge Complete!</h2>
            <p>Your CAPE-enhanced learning session is complete.</p>
            <button
              onClick={() => setCurrentStep('dashboard')}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                padding: '15px 30px',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: 'pointer',
                marginTop: '15px'
              }}
            >
              Start Another Challenge
            </button>
          </div>
        )}
      </ContentContainer>
    </InterfaceOverlay>
  );
};

export default CompetitionInterface;
